import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { EllipsisVertical } from "lucide-react";
import {
  useCallback,
  useMemo,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { SPRING } from "@/components/layout/sidebar/sidebar.constants";
import { useLocale } from "@/hooks/useLocale";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/utils/cn";

import {
  MAYA_SIDEBAR_ID,
  SIDEBAR_OVERLAY_WIDTH_PX,
  STORAGE_KEYS,
} from "../constants";
import { ChatProvider } from "../context/chat-provider";
import { MayaSpaceContext } from "../context/space-context";
import { useChat } from "../context/use-chat";
import { useMayaSidebar } from "../context/use-maya-sidebar";
import { useSidebarWidth } from "../hooks/use-sidebar-width";
import { MAYA_SPACES } from "../spaces";

import { ChatPanel } from "./chat-panel";

interface MayaSidebarProps {
  isDesktop: boolean;
}

const INSTANT = { duration: 0 } as const;

/**
 * The sidebar's frame: a faint aurora border that saturates and spins while
 * Maya writes (`data-state`), over the assistant canvas (brand radials + a
 * fading dot grid). Inside ChatProvider so it can read the thinking state.
 */
const MayaShell = ({
  floating = false,
  children,
}: {
  floating?: boolean;
  children: ReactNode;
}) => {
  const { isTyping } = useChat();
  return (
    <div
      data-state={isTyping ? "thinking" : "idle"}
      data-floating={floating || undefined}
      className="maya-shell h-full"
    >
      <div className="maya-shell-surface">
        <div className="bg-assistant-canvas" aria-hidden="true" />
        <div className="relative z-10 h-full">{children}</div>
      </div>
    </div>
  );
};

/**
 * Maya's chat as a sidebar beside the page, so the page stays visible (and is
 * sent along as context) while the user asks about it.
 *
 * On desktop it is an in-flow card at the inline-end of the layout row, after
 * the page, resizable from its inline-start edge (the one facing the page).
 * Below `lg` it slides in over the page as a drawer from the inline-end edge.
 * The content fades in from a blur as the card unfolds, so opening Maya
 * feels like something arriving rather than a panel sliding.
 *
 * `ChatProvider` sits above both layouts and stays mounted while closed, so a
 * reply that is still streaming survives closing the sidebar or crossing the
 * `lg` breakpoint. Crossing between My Space and Workspace opens the other
 * space's chat instead; a reply still streaming there is saved by the server
 * and shows when that space's chat is next opened. Focus on close is handled
 * by the launcher, which can only take it back once the warp has landed (see
 * MayaCopilot).
 */
export const MayaSidebar = ({ isDesktop }: MayaSidebarProps) => {
  const { t } = useTranslation(["common"]);
  const { open, close, sidebarRef, space } = useMayaSidebar();
  const { isRtl } = useLocale();
  const reducedMotion = usePrefersReducedMotion();
  const {
    width,
    resizing,
    minWidth,
    maxWidth,
    startResize,
    onResizeKeyDown,
    resetWidth,
  } = useSidebarWidth({ storageKey: STORAGE_KEYS.sidebarWidth, isRtl });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "Escape" && !e.defaultPrevented) close();
    },
    [close],
  );

  // Memoised so that dragging the edge, resizing the window or a warp phase
  // change re-renders only this frame — never the chat (and every message's
  // markdown) inside it.
  const chat = useMemo(
    () => (
      <MayaShell floating={!isDesktop}>
        <ChatPanel onClose={close} />
      </MayaShell>
    ),
    [isDesktop, close],
  );

  // Arrives from the side it docks on, out of a blur.
  const contentMotion = useMemo(
    () =>
      reducedMotion
        ? {}
        : {
            initial: { opacity: 0, x: isRtl ? -24 : 24, filter: "blur(8px)" },
            animate: {
              opacity: 1,
              x: 0,
              filter: "blur(0px)",
              // A leftover `filter` would make this a containing block for
              // any fixed-position descendant, so drop it once it has cleared.
              transitionEnd: { filter: "none" },
            },
            transition: {
              duration: 0.35,
              delay: 0.08,
              ease: [0.22, 1, 0.36, 1] as const,
            },
          },
    [reducedMotion, isRtl],
  );

  const offscreen = isRtl ? "-110%" : "110%";

  return (
    // A new space is a new chat — its own messages, list and endpoints — so
    // everything below starts over when the page crosses spaces.
    <MayaSpaceContext.Provider key={space} value={MAYA_SPACES[space]}>
      <ChatProvider>
        {isDesktop ? (
          <AnimatePresence initial={false}>
            {open && (
              <motion.aside
                key="maya-docked"
                ref={sidebarRef}
                id={MAYA_SIDEBAR_ID}
                aria-label={t("common:a11y.maya_copilot")}
                initial={{ width: 0, opacity: 0 }}
                animate={{ width, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={resizing || reducedMotion ? INSTANT : SPRING}
                onKeyDown={handleKeyDown}
                // Not clipped itself (the clip is the inner wrapper), so the
                // resize grip can sit across the card's edge.
                className={cn(
                  "relative h-full shrink-0",
                  resizing && "select-none",
                )}
              >
                <div className="h-full overflow-hidden rounded-xl">
                  {/* Fixed-width content: the card animates its width, the chat
                    inside never reflows mid-animation. */}
                  <motion.div
                    className="h-full"
                    style={{ width }}
                    {...contentMotion}
                  >
                    {chat}
                  </motion.div>
                </div>
                {/* Resize handle on the edge facing the page: a strip that
                  straddles the edge (grab anywhere along it), with a ⋮ grip
                  centred on the border so it's discoverable. Arrow / Home /
                  End keys when focused; double-click restores the default. */}
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={t("common:a11y.resize_maya_sidebar")}
                  aria-valuenow={width}
                  aria-valuemin={minWidth}
                  aria-valuemax={maxWidth}
                  tabIndex={0}
                  title={t("common:a11y.drag_to_resize")}
                  onPointerDown={startResize}
                  onKeyDown={onResizeKeyDown}
                  onDoubleClick={resetWidth}
                  className="group/resize absolute inset-y-0 -start-2 z-20 w-4 cursor-ew-resize touch-none focus-visible:outline-hidden"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute start-0.5 top-1/2 grid h-9 w-3 -translate-y-1/2 place-items-center rounded-full border border-border bg-surface text-muted-foreground shadow-sm transition-colors",
                      "group-hover/resize:border-primary/40 group-hover/resize:text-primary",
                      // Keyboard focus: a clear ring on the grip itself.
                      "group-focus-visible/resize:border-primary group-focus-visible/resize:text-primary group-focus-visible/resize:ring-2 group-focus-visible/resize:ring-ring",
                      resizing && "border-primary/40 text-primary",
                    )}
                  >
                    <EllipsisVertical className="size-3" />
                  </span>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>
        ) : (
          <AnimatePresence>
            {open && (
              <motion.div
                key="maya-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={reducedMotion ? INSTANT : { duration: 0.2 }}
                aria-hidden="true"
                onClick={close}
                // z-45: same layer as the launcher, below modal overlays (z-50).
                className="fixed inset-0 z-45 bg-black/40"
              />
            )}
            {open && (
              <motion.aside
                key="maya-overlay"
                ref={sidebarRef}
                id={MAYA_SIDEBAR_ID}
                aria-label={t("common:a11y.maya_copilot")}
                initial={{ x: offscreen }}
                animate={{ x: 0 }}
                exit={{ x: offscreen }}
                transition={reducedMotion ? INSTANT : SPRING}
                onKeyDown={handleKeyDown}
                // Floats with a small gutter instead of sitting flush on the edge.
                style={{
                  width: `min(calc(100vw - 16px), ${SIDEBAR_OVERLAY_WIDTH_PX}px)`,
                }}
                className="fixed inset-y-2 end-2 z-45"
              >
                {chat}
              </motion.aside>
            )}
          </AnimatePresence>
        )}
      </ChatProvider>
    </MayaSpaceContext.Provider>
  );
};
